class Carousel {
  constructor(
    root,
    { loop = false, slidesPerView = 1, swipeThresholdPx = 40 } = {}
  ) {
    this.root = root;
    this.viewport = root.querySelector("[data-carousel-viewport]");
    this.track = root.querySelector("[data-carousel-track]");
    this.prevBtn = root.querySelector("[data-carousel-prev]");
    this.nextBtn = root.querySelector("[data-carousel-next]");
    this.dotsWrap = root.querySelector("[data-carousel-dots]");
    this.status = root.querySelector("[data-carousel-status]");

    this.loop = loop;
    this.swipeThresholdPx = swipeThresholdPx;

    // optional responsive multi-slide view via CSS attribute
    this.root.dataset.slidesPerView = String(slidesPerView);

    this.index = 0;
    this.slides = Array.from(root.querySelectorAll("[data-carousel-slide]"));

    // drag state
    this.isPointerDown = false;
    this.pointerId = null;
    this.startX = 0;
    this.currentX = 0;
    this.startTranslate = 0;
    this.hasDragged = false;

    this.onResize = this.onResize.bind(this);
    this.onPrev = this.onPrev.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);

    this.init();
  }

  init() {
    // Make slides accessible-ish
    this.slides.forEach((slide, i) => {
      slide.setAttribute("role", "group");
      slide.setAttribute("aria-roledescription", "diapositive");
      slide.setAttribute("aria-label", `${i + 1} de ${this.slides.length}`);
    });

    this.buildDots();
    this.measure();
    this.goTo(0, {
      announce: true,
      focus: false,
    });

    this.prevBtn?.addEventListener("click", this.onPrev);
    this.nextBtn?.addEventListener("click", this.onNext);
    this.viewport?.addEventListener("keydown", this.onKeydown);

    // Pointer events cover touch + mouse
    this.viewport?.addEventListener("pointerdown", this.onPointerDown, {
      passive: false,
    });
    window.addEventListener("pointermove", this.onPointerMove, {
      passive: false,
    });
    window.addEventListener("pointerup", this.onPointerUp, {
      passive: true,
    });
    window.addEventListener("pointercancel", this.onPointerUp, {
      passive: true,
    });

    window.addEventListener("resize", this.onResize);
  }

  destroy() {
    window.removeEventListener("resize", this.onResize);
    this.prevBtn?.removeEventListener("click", this.onPrev);
    this.nextBtn?.removeEventListener("click", this.onNext);
    this.viewport?.removeEventListener("keydown", this.onKeydown);

    this.viewport?.removeEventListener("pointerdown", this.onPointerDown);
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointercancel", this.onPointerUp);
  }

  buildDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.innerHTML = "";

    // Calculate effective number of pages
    // If maxScroll exists and is less than what all slides would need,
    // we have fewer "pages" than slides
    const numPages = this.calculateNumPages();

    this.dots = Array.from({ length: numPages }, (_, i) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "carousel__dot";
      btn.setAttribute("aria-label", `Aller à la diapositive ${i + 1}`);
      btn.addEventListener("click", () =>
        this.goTo(i, {
          announce: true,
        })
      );
      this.dotsWrap.appendChild(btn);
      return btn;
    });
  }

  calculateNumPages() {
    if (!this.slideStep || this.slideStep === 0) {
      return this.slides.length;
    }

    // Calculate how many "pages" we actually have based on maxScroll
    // Each page is one slideStep, except the last one which might be partial
    const fullPages = Math.floor(this.maxScroll / this.slideStep);
    const hasPartialPage = this.maxScroll % this.slideStep > 0;

    // Add 1 for the first page (index 0)
    const totalPages = fullPages + (hasPartialPage ? 1 : 0) + 1;

    // Never exceed the number of slides
    return Math.min(totalPages, this.slides.length);
  }

  measure() {
    // Get computed style to check for gaps
    const trackStyle = window.getComputedStyle(this.track);
    const gap = parseFloat(trackStyle.gap) || 0;

    // Slide width including gap
    this.slideStep =
      (this.slides[0]?.offsetWidth || this.viewport.clientWidth) + gap;

    // Calculate the maximum scrollable distance
    const trackWidth = this.track.scrollWidth;
    const viewportWidth = this.viewport.clientWidth;
    this.maxScroll = Math.max(0, trackWidth - viewportWidth);

    this.maxIndex = this.slides.length - 1;

    // Rebuild dots after measuring since number of pages might change
    this.buildDots();

    this.updateUI();
    this.snapToIndex({
      animate: false,
    });
  }

  translateTo(x, { animate = true } = {}) {
    // Clamp x to prevent scrolling past the end - do it here instead
    x = Math.min(x, this.maxScroll);
    x = Math.max(0, x); // Also prevent negative scroll

    if (!animate) this.track.style.transition = "none";
    else this.track.style.transition = "";

    this.track.style.transform = `translate3d(${-x}px, 0, 0)`;
  }

  getXForIndex(i) {
    const x = i * this.slideStep;
    return x;
  }

  snapToIndex({ animate = true } = {}) {
    const x = this.getXForIndex(this.index);
    this.translateTo(x, {
      animate,
    });
  }

  goTo(i, { announce = false, focus = false } = {}) {
    if (this.loop) {
      if (i < 0) i = this.maxIndex;
      if (i > this.maxIndex) i = 0;
    } else {
      i = Math.max(0, Math.min(this.maxIndex, i));
    }

    this.index = i;
    this.snapToIndex({
      animate: true,
    });
    this.updateUI();

    if (announce) this.announce();
    if (focus) this.viewport?.focus();
  }

  updateUI() {
    // Get the actual current translate position
    const currentScroll = this.index * this.slideStep;
    const clampedScroll = Math.min(currentScroll, this.maxScroll);

    const atEnd =
      clampedScroll >= this.maxScroll - 1 || this.index === this.maxIndex;
    const atStart = this.index === 0;

    if (this.prevBtn) this.prevBtn.disabled = !this.loop && atStart;
    if (this.nextBtn) this.nextBtn.disabled = !this.loop && atEnd;

    if (this.dots) {
      this.dots.forEach((d, i) =>
        d.classList.toggle("is-active", i === this.index)
      );
    }
  }

  announce() {
    if (!this.status) return;
    this.status.textContent = `Diapositive ${this.index + 1} de ${
      this.slides.length
    }`;
  }

  onPrev() {
    if (this.index === 0 && !this.loop) {
      return;
    }

    this.goTo(this.index - 1, {
      announce: true,
      focus: true,
    });
  }

  onNext() {
    if (this.index >= this.maxIndex && !this.loop) {
      return;
    }

    this.goTo(this.index + 1, {
      announce: true,
      focus: true,
    });
  }

  onKeydown(e) {
    if (e.key === "ArrowLeft") this.onPrev();
    if (e.key === "ArrowRight") this.onNext();
  }

  onResize() {
    // Re-measure positions after layout changes
    this.measure();
  }

  onPointerDown(e) {
    // Only left click for mouse
    if (e.pointerType === "mouse" && e.button !== 0) return;

    this.isPointerDown = true;
    this.pointerId = e.pointerId;
    this.startX = e.clientX;
    this.currentX = e.clientX;
    this.hasDragged = false;

    this.startTranslate = this.getXForIndex(this.index);

    // Prevent the browser's native drag-and-drop on links/images inside
    // slides, which otherwise hijacks the pointer sequence mid-swipe.
    e.preventDefault();
  }

  onPointerMove(e) {
    if (!this.isPointerDown || e.pointerId !== this.pointerId) return;

    const dx = e.clientX - this.startX;

    // Mark as dragged if moved more than a few pixels
    if (Math.abs(dx) > 5) {
      if (!this.hasDragged) {
        this.hasDragged = true;
        this.root.classList.add("is-dragging");

        this.viewport.setPointerCapture?.(this.pointerId);
      }
      e.preventDefault();
    }

    this.currentX = e.clientX;

    const nextTranslate = this.startTranslate - dx;
    this.translateTo(nextTranslate, {
      animate: false,
    });
  }

  onPointerUp(e) {
    if (!this.isPointerDown || e.pointerId !== this.pointerId) return;

    this.isPointerDown = false;
    this.root.classList.remove("is-dragging");

    const dx = this.currentX - this.startX;

    // Only handle swipe if user actually dragged
    if (this.hasDragged && Math.abs(dx) >= this.swipeThresholdPx) {
      if (dx < 0)
        this.goTo(this.index + 1, {
          announce: true,
        });
      else
        this.goTo(this.index - 1, {
          announce: true,
        });
    } else if (this.hasDragged) {
      this.snapToIndex({
        animate: true,
      });
    }

    // Release pointer capture if we captured it
    if (this.hasDragged && this.viewport.hasPointerCapture?.(this.pointerId)) {
      this.viewport.releasePointerCapture?.(this.pointerId);
    }

    this.pointerId = null;
    this.hasDragged = false;
  }
}

document.addEventListener("DOMContentLoaded", function () {
  document.querySelectorAll("[data-carousel]").forEach((el) => {
    new Carousel(el, {
      loop: false,
      slidesPerView: 1,
    });
  });
});
